import RefreshIcon from '@mui/icons-material/Refresh';
import Logger from "../../../services/Logger";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomSnackbar from "../../components/CustomSnackbar";
import GenImagePreviewService from "./GenImagePreviewService";
export default function GenImagePreviewMain() {
    const [puzzleType, setPuzzleType] = useState('WORD_SEARCH');
    const [puzzleId, setPuzzleId] = useState('');
    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('info');
    const [imageUrl, setImageUrl] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);

    const handleTypeChange = (event) => {
        setPuzzleType(event.target.value);
        setImageUrl(''); // Clear image when type changes
        setRefreshKey(0);
    };

    const handleIdChange = (event) => {
        setPuzzleId(event.target.value);
        setImageUrl(''); // Clear image when ID changes
        setRefreshKey(0);
    };

    // Fetch puzzle data when puzzleId is not empty
    useEffect(() => {
        const fetchPuzzle = async () => {
            if (!puzzleId.trim()) {
                setImageUrl('');
                return;
            }

            try {
                const puzzle = await GenImagePreviewService.getPuzzle(puzzleType, puzzleId);
                
                // Extract previewImageUrl - check root first (for grid puzzles), then template (for crossword)
                const previewImageUrl = puzzle?.previewImageUrl;
                
                if (previewImageUrl) {
                    setImageUrl(previewImageUrl);
                }
            } catch (error) {
                Logger.error('Error fetching puzzle:', error);
                // Don't set error here, just log it
            }
        };

        fetchPuzzle();
    }, [puzzleId, puzzleType, refreshKey]);

    const handleGenerate = async () => {
        if (!puzzleId.trim()) {
            setSnackbarMessage('Please enter a puzzle ID');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setLoading(true);
        setImageUrl('');

        try {
            // Call the generate image endpoint
            await GenImagePreviewService.generatePuzzleImage(puzzleType, puzzleId);
            setSnackbarMessage('Preview image generated successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            
            // Fetch the puzzle object again to get the updated previewImageUrl
            const puzzle = await GenImagePreviewService.getPuzzle(puzzleType, puzzleId);
            
            // Extract previewImageUrl - check root first (for grid puzzles), then template (for crossword)
            const previewImageUrl = puzzle?.previewImageUrl || puzzle?.template?.previewImageUrl;
            
            if (previewImageUrl) {
                setImageUrl(previewImageUrl);
                setRefreshKey(prev => prev + 1);
            } else {
                // Fallback to default URL if previewImageUrl is not available
                let previewUrl = '';
                if (puzzleType === 'CROSSWORD') {
                    previewUrl = `/home/cross/${puzzleId}`;
                } else {
                    // WORD_SEARCH and HIDDEN_MESSAGE both use /home/grid
                    previewUrl = `/home/grid/${puzzleId}`;
                }
                setImageUrl(previewUrl);
                setRefreshKey(prev => prev + 1);
            }
        } catch (error) {
            Logger.error('Error generating preview image:', error);
            setSnackbarMessage('Failed to generate preview image: ' + (error.message || 'Unknown error'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        if (imageUrl) {
            setRefreshKey(prev => prev + 1);
        }
    };

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Generate Preview Image
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Generate preview images for puzzles. Select the puzzle type and enter the puzzle ID.
            </Typography>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Puzzle Information
                            </Typography>
                            
                            <Box sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                    <FormControl size="small" sx={{ minWidth: 150, maxWidth: 200 }}>
                                        <InputLabel id="puzzle-type-label">Puzzle Type</InputLabel>
                                        <Select
                                            labelId="puzzle-type-label"
                                            value={puzzleType}
                                            label="Puzzle Type"
                                            onChange={handleTypeChange}
                                        >
                                            <MenuItem value="WORD_SEARCH">Word Search</MenuItem>
                                            <MenuItem value="HIDDEN_MESSAGE">Hidden Message</MenuItem>
                                            <MenuItem value="CROSSWORD">Crossword</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        size="small"
                                        sx={{ flex: 1 }}
                                        label="Puzzle ID (UUID)"
                                        value={puzzleId}
                                        onChange={handleIdChange}
                                        placeholder="Enter puzzle UUID"
                                    />

                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleGenerate}
                                        disabled={loading || !puzzleId.trim()}
                                    >
                                        {loading ? (
                                            <>
                                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                                Generating...
                                            </>
                                        ) : (
                                            'Generate'
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">
                                    Preview Image
                                </Typography>
                                {imageUrl && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<RefreshIcon />}
                                        onClick={handleRefresh}
                                    >
                                        Refresh
                                    </Button>
                                )}
                            </Box>

                            {imageUrl ? (
                                <Box
                                    sx={{
                                        width: '100%',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        backgroundColor: '#f5f5f5',
                                        minHeight: '400px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative'
                                    }}
                                >
                                    <img
                                        key={refreshKey}
                                        src={imageUrl}
                                        alt="Puzzle Preview"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            display: 'block'
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '400px',
                                        border: '1px dashed #e0e0e0',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#fafafa'
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        Generate a preview image to see it here
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />
        </Box>
    );
}

